import { Paper, Stack, Typography } from '@mui/material';
import React, { useCallback, useMemo } from 'react';
import useDashboardQuery from '../../../hooks/useDashboardQuery';
import TeamMemberCard from '../../molecules/dashboard-details-v2/TeamMemberCard';

const TeamMembersCard: React.FC<{}> = () => {
    const { queryAnswer: { answeredQuestion: { answer: keyPeople } } } = useDashboardQuery('Key People');

    const getRoleRank = useCallback((role: string): number => {
        const roleUpper = role?.toUpperCase() || '';

        // Rank 1 – Founder (ownership) / CEO (highest executive)
        if (roleUpper.includes('FOUNDER') || roleUpper.includes('CEO')) return 1;
        // Rank 2 – President (if separate from CEO)
        if (roleUpper.includes('PRESIDENT') && !roleUpper.includes('VICE')) return 2;
        // Rank 3 – All C-suite (COO, CFO, CTO, CMO, CHRO, etc.)
        if (roleUpper.includes('CHIEF') ||
            (roleUpper.includes('OFFICER') && (roleUpper.includes('CFO') || roleUpper.includes('COO') || roleUpper.includes('CTO') || roleUpper.includes('CMO') || roleUpper.includes('CPO') || roleUpper.includes('CRO') || roleUpper.includes('CHRO') || roleUpper.includes('CDO') || roleUpper.includes('CIO') || roleUpper.includes('CSO') || roleUpper.includes('CCO'))) ||
            /^C[A-Z]O\b/.test(roleUpper)) return 3;
        // Rank 4 – Executive VP / Senior VP
        if ((roleUpper.includes('EXECUTIVE') && roleUpper.includes('VP')) ||
            (roleUpper.includes('SENIOR') && roleUpper.includes('VP')) ||
            (roleUpper.includes('EXECUTIVE') && roleUpper.includes('VICE PRESIDENT')) ||
            (roleUpper.includes('SENIOR') && roleUpper.includes('VICE PRESIDENT'))) return 4;
        // Rank 5 – VP
        if (roleUpper.includes('VICE PRESIDENT') || roleUpper.includes('VP')) return 5;
        // Rank 6 – Head of / Director
        if (roleUpper.includes('HEAD OF') || roleUpper.includes('DIRECTOR')) return 6;
        // Rank 7 – Senior Managers / Senior Specialists
        if (roleUpper.includes('SENIOR')) return 7;
        // Rank 8 – Mid-level & Junior roles
        if (roleUpper.includes('MANAGER') || roleUpper.includes('LEAD') || roleUpper.includes('SPECIALIST') ||
            roleUpper.includes('ANALYST') || roleUpper.includes('COORDINATOR') || roleUpper.includes('JUNIOR')) return 8;
        // Rank 9 – Administrative & Support staff
        if (roleUpper.includes('SECRETARY') || roleUpper.includes('ASSISTANT') || roleUpper.includes('CLERK') ||
            roleUpper.includes('ADMIN') || roleUpper.includes('SUPPORT')) return 9;

        // All others (default)
        return 10;
    }, []);

    const sortedKeyPeople = useMemo(() => {
        if (Array.isArray(keyPeople)) {
            return keyPeople.sort((a: any, b: any) => {
                const rankA = getRoleRank(a.role);
                const rankB = getRoleRank(b.role);

                if (rankA !== rankB)
                    return rankA - rankB;

                if (a.role < b.role) return -1;
                if (a.role > b.role) return 1;

                return 0;
            });
        }

        return [];
    }, [getRoleRank, keyPeople]);

	const members = useMemo(() => sortedKeyPeople?.map((person: any) => ({
		name: person.title.split('|')?.[0]?.split('-')?.[0],
		title: person.role,
		email: person.email,
		background: person.content,
		initials: person.title ? person.title.split(' ').slice(0, 2).map((word: string) => word.charAt(0).toUpperCase()).join('') : '',
	})) || [], [sortedKeyPeople]);

	return (
		<Paper elevation={0} sx={{ p: 3, borderRadius: 1 }}>
			<Typography fontSize={16} fontWeight={600} color="#2c3e50" mb={2}>
				{'Team Members'}
			</Typography>
			<Stack spacing={2.5}>
				{members.map((member, index) => (
					<TeamMemberCard key={index} member={member} />
				))}
			</Stack>
		</Paper>
	);
};

export default TeamMembersCard;